import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Eye, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { getUsers } from "core/Services/AdminServices";
import { useTranslation } from "react-i18next";

interface Merchant {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  business_name?: string;
  active: boolean;
}

const OrderImport: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadMerchants();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = merchants.filter(
        (m) =>
          m.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          m.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (m.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
      );
      setFilteredMerchants(filtered);
    } else {
      setFilteredMerchants(merchants);
    }
  }, [searchTerm, merchants]);

  const loadMerchants = async () => {
    try {
      setIsLoading(true);
      const response = await getUsers({ role: "merchant", getAll: 1 });
      setMerchants(response.data || []);
      setFilteredMerchants(response.data || []);
    } catch (error) {
      toast.error("Failed to load merchants");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMerchantClick = (merchantId: number) => {
    navigate(`/admin-merchant-dashboard/${merchantId}`);
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <h1 className="text-2xl font-bold">{t("menu.orderImport")}</h1>
      <p className="text-gray-600">Select a merchant to view orders and import new orders</p>

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={t("merchants.searchPlaceholder")}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Merchants Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("merchants.name")}
              </th>
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("merchants.businessName")}
              </th>
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("merchants.email")}
              </th>
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("merchants.phoneNumber")}
              </th>
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("merchants.status")}
              </th>
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("merchants.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                  <p className="mt-2 text-gray-500">Loading merchants...</p>
                </td>
              </tr>
            ) : filteredMerchants.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-gray-500">
                  {t("merchants.noMerchantsFound")}
                </td>
              </tr>
            ) : (
              filteredMerchants.map((merchant) => (
                <tr
                  key={merchant.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleMerchantClick(merchant.id)}
                >
                  <td className="p-3">
                    {merchant.first_name} {merchant.last_name}
                  </td>
                  <td className="p-3">{merchant.business_name || "-"}</td>
                  <td className="p-3">{merchant.email}</td>
                  <td className="p-3">{merchant.phone_number}</td>
                  <td className="p-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        merchant.active
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {merchant.active ? t("merchants.active") : t("merchants.deactivated")}
                    </span>
                  </td>
                  <td className="p-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMerchantClick(merchant.id);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrderImport;
